import React, {useEffect, useState, useMemo} from 'react'
import axios from 'axios'
import Select, {MultiValue} from 'react-select'
import {KTSVG} from '../../../../_metronic/helpers'
import {DeleteConfirmationModal} from '../../../common/Models/confirmation-modal/DeleteConfirmationModal'
import toaster from '../../../../Utils/toaster'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import {CommonLoading} from '../../../../Utils/commonLoading'
import {useSelector} from 'react-redux'
import {RootState} from '../../../redux/store'

const API_URL = process.env.REACT_APP_API_URL

const REASON_MAX_LENGTH = 500

interface ExcludeCustomersViewProps {
  userId: string
}

interface ExcludedCustomer {
  code: number
  fK_Customer: number
  customerName: string
  customerEmail: string
  isDeleted: boolean
  reason: string | null
}

interface CustomerOption {
  value: number
  label: string
}

export const ExcludeCustomersView: React.FC<ExcludeCustomersViewProps> = ({userId}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [excludedCustomers, setExcludedCustomers] = useState<ExcludedCustomer[]>([])
  const [selectedCustomers, setSelectedCustomers] = useState<MultiValue<CustomerOption>>([])
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Get customers from Redux global state (same as appointment filters)
  const {items: globalCustomers, loading: isFetchingCustomers} = useSelector(
    (state: RootState) => state.customers
  )
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  
  // Delete modal state
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [currentItemCode, setCurrentItemCode] = useState<number | null>(null)

  // Transform global customers to options and filter out already excluded ones
  const availableCustomers = useMemo(() => {
    const excludedCustomerIds = excludedCustomers.map(ec => ec.fK_Customer)
    return globalCustomers
      ?.filter((c: any) => !excludedCustomerIds.includes(Number(c.key)))
      .map((c: any) => ({
        value: Number(c.key),
        label: c.value ?? '',
      })) ?? []
  }, [globalCustomers, excludedCustomers])

  // Fetch excluded customers for this interpreter
  const fetchExcludedCustomers = async (page: number, itemsPerPage: number) => {
    setIsLoading(true)
    try {
      const response = await axios.get(`${API_URL}/customer/interpreter/${userId}/excluded-customers`, {
        params: {
          page: page,
          items_per_page: itemsPerPage,
        },
      })
      const {data, count} = response.data
      setExcludedCustomers(data || [])
      setTotalItems(count || 0)
      setTotalPages(Math.ceil((count || 0) / itemsPerPage))
    } catch (error) {
      console.error('Error fetching excluded customers:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      fetchExcludedCustomers(currentPage, rowsPerPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentPage, rowsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setCurrentPage(1)
  }

  const resetFormFields = () => {
    setSelectedCustomers([])
    setReason('')
  }

  const handleSubmit = async () => {
    if (!selectedCustomers || selectedCustomers.length === 0) {
      toaster('error', 'Please select at least one customer')
      return
    }

    setIsSubmitting(true)
    const data = {
      fK_User: userId,
      fK_Customers: selectedCustomers.map(item => item.value),
      reason: reason.trim() || null,
    }

    try {
      await axios.post(`${API_URL}/customer/interpreter-excludes-bulk`, data)
      toaster('success', `Successfully added to ${selectedCustomers.length} customer exclusion list(s)`)
      fetchExcludedCustomers(currentPage, rowsPerPage)
      resetFormFields()
    } catch (error) {
      toaster('error', 'Failed to add to exclusion lists!')
    } finally {
      setIsSubmitting(false)
    }
  }

  const openDeleteModal = (code: number) => {
    setCurrentItemCode(code)
    setDeleteModalOpen(true)
  }

  const handleDeleteSubmit = async () => {
    if (currentItemCode) {
      await handleDelete(currentItemCode)
      setDeleteModalOpen(false)
      setCurrentItemCode(null)
    }
  }

  const handleDelete = async (code: number) => {
    try {
      await axios.put(`${API_URL}/customer/excludes`, {
        code: code,
        isDeleted: true,
      })
      toaster('success', 'Successfully removed from exclusion list')
      fetchExcludedCustomers(currentPage, rowsPerPage)
    } catch (error) {
      toaster('error', 'Failed to remove from exclusion list')
    }
  }

  return (
    <>
      {/* Add Customers Modal */}
      <div className='modal fade' tabIndex={-1} id='kt_add_customer_exclusion_modal'>
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header py-2'>
              <div className='d-flex flex-column'>
                <h4 className='modal-title'>Add to Customer Exclusion Lists</h4>
                <span className='text-gray-500' style={{fontSize: '12px'}}>
                  Select customers where this interpreter should be excluded from calls
                </span>
              </div>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                data-bs-dismiss='modal'
                aria-label='Close'
                onClick={resetFormFields}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>
            <div className='modal-body'>
              <div className='row g-4'>
                <div className='col-12'>
                  <label className='form-label'>Customers</label>
                  <span className='text-gray-500 ms-2' style={{fontSize: '11px'}}>
                    (Select multiple customers)
                  </span>
                  <Select
                    className='react-select-styled react-select-solid react-select-sm'
                    classNamePrefix='react-select'
                    options={!isFetchingCustomers ? availableCustomers : []}
                    placeholder='Search and select customers...'
                    isLoading={isFetchingCustomers}
                    styles={{
                      control: (provided: any) => ({
                        ...provided,
                        width: '100%',
                        border: '1px solid #e4e6ef',
                        minHeight: '38px',
                      }),
                      multiValue: (provided: any) => ({
                        ...provided,
                        backgroundColor: '#e1f0ff',
                      }),
                      multiValueLabel: (provided: any) => ({
                        ...provided,
                        color: '#3699ff',
                      }),
                      multiValueRemove: (provided: any) => ({
                        ...provided,
                        color: '#3699ff',
                        ':hover': {
                          backgroundColor: '#3699ff',
                          color: 'white',
                        },
                      }),
                    }}
                    onChange={(newValue: MultiValue<CustomerOption>) => setSelectedCustomers(newValue)}
                    value={selectedCustomers}
                    isMulti
                    closeMenuOnSelect={false}
                    isClearable
                  />
                </div>
                <div className='col-12'>
                  <label className='form-label'>Reason</label>
                  <span className='text-gray-500 ms-2' style={{fontSize: '11px'}}>
                    (Optional)
                  </span>
                  <textarea
                    className='form-control form-control-sm'
                    rows={3}
                    maxLength={REASON_MAX_LENGTH}
                    placeholder='Reason for excluding this interpreter from the selected customer(s)'
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className='text-gray-500 text-end' style={{fontSize: '11px'}}>
                    {reason.length}/{REASON_MAX_LENGTH}
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-footer py-3'>
              <button
                type='button'
                className='btn btn-light btn-sm'
                data-bs-dismiss='modal'
                onClick={resetFormFields}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary btn-sm'
                data-bs-dismiss='modal'
                onClick={handleSubmit}
                disabled={!selectedCustomers || selectedCustomers.length === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                    Adding...
                  </>
                ) : (
                  <>Add {selectedCustomers && selectedCustomers.length > 0 ? `(${selectedCustomers.length})` : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='card-body p-0'>
        <div className='row g-1 g-xl-4'>
          <div className='col-md-12 col-sm-12'>
            <div className='border border-gray-500 border-dotted rounded p-5 mb-3'>
              <div className='d-flex justify-content-between align-items-center w-100'>
                <div className='d-flex flex-column'>
                  <div className='d-flex fs-3 fw-semibold'>Customer Exclusion List</div>
                  <span className='text-gray-500' style={{fontSize: '12px'}}>
                    This interpreter won't receive calls from these customers
                  </span>
                </div>

                <div className='d-flex align-items-center'>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary btn-icon'
                    data-bs-toggle='modal'
                    data-bs-target='#kt_add_customer_exclusion_modal'
                  >
                    <i className='bi bi-plus fs-2'></i>
                  </button>
                </div>
              </div>

              <div className='row g-4'>
                <div className='py-0 pt-3'>
                  <div className='table-responsive'>
                    <table className='table table-striped table-rounded table-hover table-row-gray-300 align-middle gs-0 gy-2'>
                      <thead>
                        <tr className='fw-semibold text-muted text-uppercase'>
                          <th className='min-w-200px'>Customer Name</th>
                          <th className='min-w-200px'>Email</th>
                          <th className='min-w-150px'>Reason</th>
                          <th className='min-w-100px text-end'>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excludedCustomers?.length > 0 ? (
                          excludedCustomers.map((item) => (
                            <tr key={item.code} style={{cursor: 'pointer'}}>
                              <td>
                                <span className='text-gray-800 text-hover-primary fs-6'>
                                  {item.customerName}
                                </span>
                              </td>
                              <td>
                                <span className='text-gray-800 text-hover-primary fs-6'>
                                  {item.customerEmail}
                                </span>
                              </td>
                              <td>
                                {item.reason ? (
                                  <span className='text-gray-800 fs-6'>{item.reason}</span>
                                ) : (
                                  <span className='text-gray-400 fs-6'>-</span>
                                )}
                              </td>
                              <td className='text-end'>
                                <button
                                  className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
                                  type='button'
                                  onClick={() => openDeleteModal(item.code)}
                                >
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen027.svg'
                                    className='svg-icon-3'
                                  />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4}>
                              <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                                <div className='text-center'>
                                  <div className='symbol symbol-200px'>
                                    <img src='/media/other/nodata.png' alt='' />
                                  </div>
                                </div>
                                <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                                  No exclusions found
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {isLoading && <CommonLoading />}

                {totalItems > 0 && (
                  <div className='pt-3'>
                    <CommonPaginationModel
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      total={totalItems}
                      onRowsPerPageChange={handleRowsPerPageChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          title='Confirmation'
          content={'Are you sure you would like to remove this customer from the exclusion list?'}
          setOpen={setDeleteModalOpen}
          onSubmit={handleDeleteSubmit}
        />
      )}
    </>
  )
}

export default ExcludeCustomersView
